import { NavLink } from "react-router-dom";
import { useMemo } from "react";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import { computeEntitlement, ANNUAL_SAVING } from "@/lib/billing";
import { Sparkles, AlertTriangle, CreditCard } from "lucide-react";

// Slim, high-contrast banner: warns as the trial nears its end, flags a failed
// renewal (with the grace countdown), and hard-blocks an expired trial. Nudges
// admins toward the annual plan's saving. Hidden while a subscription is active
// or when there's plenty of trial left.
export default function TrialBanner() {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const ent = useMemo(() => computeEntitlement(activeSite), [activeSite]);
  const isAdmin = user?.role === "admin";

  if (!activeSite || ent.status === "active") return null;

  // Payment failed — inside the grace window (still read-write).
  if (ent.pastDue && !ent.expired) {
    const amt = activeSite.past_due_amount != null
      ? `$${(activeSite.past_due_amount / 100).toFixed(2)}${activeSite.past_due_currency ? " " + activeSite.past_due_currency.toUpperCase() : ""}`
      : null;
    const retry = activeSite.next_retry_at
      ? new Date(activeSite.next_retry_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })
      : null;
    return (
      <div
        data-testid="trial-banner-pastdue"
        className="bg-ochre-600 text-white text-sm font-semibold px-4 py-2 flex items-center justify-center gap-2 sticky top-0 z-50"
      >
        <AlertTriangle className="w-4 h-4" />
        <span data-testid="trial-banner-pastdue-text">
          {amt ? `Payment of ${amt} failed` : "Payment failed"} for {activeSite.name}
          {retry ? ` — we'll retry ${retry}.` : "."} Update your card within {ent.daysLeft} day{ent.daysLeft === 1 ? "" : "s"}.
        </span>
        {isAdmin && (
          <NavLink data-testid="trial-banner-pastdue-cta" to="/billing" className="underline ml-2 hover:no-underline font-bold">
            Update payment
          </NavLink>
        )}
      </div>
    );
  }

  if (ent.expired) {
    const reason = ent.pastDue ? "Subscription payment failed" : "Free trial ended";
    return (
      <div
        data-testid="trial-banner-expired"
        className="bg-red-600 text-white text-sm font-semibold px-4 py-2 flex items-center justify-center gap-2 sticky top-0 z-50"
      >
        <AlertTriangle className="w-4 h-4" />
        <span>{reason} for {activeSite.name} — creating records &amp; printing are paused.</span>
        {isAdmin ? (
          <NavLink data-testid="trial-banner-subscribe" to="/billing" className="underline ml-2 hover:no-underline font-bold">
            Subscribe now
          </NavLink>
        ) : (
          <span className="ml-2 opacity-90">Ask your admin to subscribe.</span>
        )}
      </div>
    );
  }

  // Trialing — only warn near the end.
  if (ent.trialing && ent.daysLeft > 10) return null;

  return (
    <div
      data-testid="trial-banner-warning"
      className="bg-ochre-500 text-white text-sm font-semibold px-4 py-2 flex items-center justify-center gap-2 sticky top-0 z-50"
    >
      <Sparkles className="w-4 h-4" />
      <span>{ent.daysLeft} day{ent.daysLeft === 1 ? "" : "s"} left in your free trial for {activeSite.name}.</span>
      {isAdmin && (
        <NavLink data-testid="trial-banner-annual-cta" to="/billing" className="inline-flex items-center gap-1 underline ml-2 hover:no-underline font-bold">
          <CreditCard className="w-3.5 h-3.5" /> Go annual &amp; save ${ANNUAL_SAVING}/yr
        </NavLink>
      )}
    </div>
  );
}
